import React, { ReactNode } from 'react';
import isEmpty from 'lodash/isEmpty';

import { I18nText, ClassName } from '../../../../../types';
import { UniversalFilterView } from '../../UniversalFilter.types';
import {
  UniversalFilterTemplateModeOption,
  UniversalFilterTemplateOnModeChange
} from './UniversalFilterTemplate.types';
import { UniversalFilterModeSelect } from '../UniversalFilterModeSelect';

import { Translate } from '../../../../Translate';

interface UniversalFilterTemplateBaseProps {
  children: ReactNode;
  filterView?: UniversalFilterView;
  i18nName: I18nText;
  className?: ClassName;
}

interface UniversalFilterTemplateWithModeProps<ModeType> {
  selectedMode: ModeType;
  modeOptions: UniversalFilterTemplateModeOption<ModeType>[];
  onModeChange: UniversalFilterTemplateOnModeChange<ModeType>;
}

interface UniversalFilterTemplateWithoutModeProps {
  selectedMode?: never;
  modeOptions?: never;
  onModeChange?: never;
}

type UniversalFilterTemplateProps<ModeType> = UniversalFilterTemplateBaseProps &
  (
    | UniversalFilterTemplateWithModeProps<ModeType>
    | UniversalFilterTemplateWithoutModeProps
  );

function UniversalFilterTemplate<ModeType>({
  children,
  filterView,
  i18nName,
  className,
  selectedMode,
  modeOptions,
  onModeChange
}: UniversalFilterTemplateProps<ModeType>) {
  if (filterView === UniversalFilterView.TABLE) {
    return (
      <>
        <td className="align-top pl-4 pr-1 md:pr-2 py-0.5">
          <div className="text-right whitespace-nowrap py-2">
            <div>
              <div className="py-px">
                <Translate id={i18nName} />
              </div>
            </div>
          </div>
        </td>
        <td className="align-top px-1 md:px-2 py-0.5">
          {!isEmpty(modeOptions) ? (
            <div className="py-px">
              <div className="relative">
                <UniversalFilterModeSelect<ModeType>
                  className="z-0 relative inline-flex items-center rounded px-2 py-2 focus:ring-offset-0 space-x-1 bg-gray-500 bg-opacity-0 hover:bg-opacity-10 whitespace-nowrap"
                  options={modeOptions}
                  selectedValue={selectedMode}
                  onChange={onModeChange}
                />
              </div>
            </div>
          ) : null}
        </td>
        <td className="px-1 md:px-2 py-0.5 w-full">
          <div className="flex-1">{children}</div>
        </td>
      </>
    );
  }

  return (
    <div className={className}>
      <div className="flex items-baseline gap-2 text-sm mb-1">
        <span className="whitespace-nowrap">
          <Translate id={i18nName} />
        </span>

        {!isEmpty(modeOptions) ? (
          <div className="whitespace-nowrap">
            <UniversalFilterModeSelect<ModeType>
              className="z-0 relative inline-flex items-center justify-between p-0 gap-1 border-b border-gray-300 dark:border-gray-700 dark:focus:border-blue-300 focus:border-blue-500 focus-within:border-blue-500 focus:ring-0 focus:ring-offset-0 w-full text-sm border-dashed focus:border-solid"
              options={modeOptions}
              selectedValue={selectedMode}
              onChange={onModeChange}
            />
          </div>
        ) : null}
      </div>
      <div className="flex flex-col gap-1">{children}</div>
    </div>
  );
}

export default UniversalFilterTemplate;
